import { useContext, useState, useEffect } from "react";
import { Modal, Input } from "antd";
import { AppStateContext } from "../../state/AppProvider";
import { appNotification } from "../../components/common/notification";
import { changePassword, userMe, logOut} from "../../api";
import { UserRole } from "../../constants/enums";

interface ChangePasswordProps{
    isOpen: boolean;
    onCloseModal: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordProps> = ({isOpen, onCloseModal}) => {
    const appStateContext = useContext(AppStateContext)
    const [oldPassword, setOldPassword] = useState<string>("");
    const [newPassword, setNewPassword] = useState<string>("");
    const [confirmNewPassword, setConfirmNewPassword] = useState<string>("");
    const [changePasswordError, setChangePasswordError] = useState<string>("");
    const [displayName, setDisplayName] = useState<string>("");

    useEffect(() => {
        if(!appStateContext?.state.loggedInAsRole) 
            window.location.href = "/login";

        const fetchDisplayName = async () => {
            const res = await userMe();
            if (res?.ok) {
                const data = await res.json();
                let user = data as User;
                setDisplayName(user.display_name ?? "");
                appStateContext?.dispatch({ type: "SET_USER_LOGIN_STATUS", payload: user.role });
            } else {
                console.log("Error fetching user info");
                window.location.href = "/login";
                appStateContext?.dispatch({ type: "SET_USER_LOGIN_STATUS", payload: undefined });
            }
        };
        fetchDisplayName();
    }, [])

    const handleChangePassword = (oldPassword: string, newPassword: string, confirmNewPassword: string) => {
        const pattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{12,}$/;
        if (!oldPassword || !newPassword || !confirmNewPassword) {
            setChangePasswordError("Please fill in all fields.")
            return;
        } else if (!pattern.test(newPassword)) {
            setChangePasswordError("Password must contain at least 12 characters, one uppercase letter, one lowercase letter, one number and one special character.");
            return;
        } else if (newPassword !== confirmNewPassword) {
            setChangePasswordError("Passwords do not match.");
            return;
        } else if (newPassword == oldPassword) {
            setChangePasswordError("Please pick a new password.");
            return;
        }
        const changePasswordAsync = async (oldPassword: string, newPassword: string, confirmNewPassword: string) => {
            const res = await changePassword(oldPassword, newPassword, confirmNewPassword);
            if (res.ok) {
                setChangePasswordError("");
                appNotification.success({ message: "Success", description: "Password changed successfully." });
                setOldPassword("");
                setNewPassword("");
                setConfirmNewPassword("");
                onCloseModal();
            } else if(res.status === 401){
                setChangePasswordError("Invalid password.");
            }else{
                setChangePasswordError("An error occurred. Please try again.");
            }
            
        }
        changePasswordAsync(oldPassword, newPassword, confirmNewPassword);
    }

    return ( 
        <>
            { (appStateContext?.state.screenSize ?? 3) > 2 ?
                (
                    <Modal
                        title={`Change Password`}
                        style={{
                            borderRadius: '16px',
                            padding: '0px',
                            boxShadow: '0px 10px 20px -8px #392EA433'
                        }}
                        className='custom-modal c-default'
                        okButtonProps={{
                            className: 'bg-primary br-16 c-root pt-2 pb-2'
                        }}
                        cancelButtonProps={{
                            className: 'bg-root br-16 c-primary b-primary pt-2 pb-2'
                        }}
                        open={isOpen}
                        onOk={() => {
                            handleChangePassword(oldPassword, newPassword, confirmNewPassword);
                        }}
                        onCancel={() => {
                            onCloseModal();
                            setOldPassword("");
                            setNewPassword("");
                            setConfirmNewPassword("");
                            setChangePasswordError("");
                        }}
                        okText="Change Password"
                        cancelText="Cancel"
                    >
                        <p style={{
                            fontSize: "16px",
                            marginTop: "0px",
                            marginBottom: '16px',
                            fontFamily: 'inter'
                        }}>Please enter your new password</p>
                        <div>
                            <span className='c-default fs16-fw500'>Old Password:</span>
                            <Input.Password className='bg-root c-default' autoComplete="none" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
                        </div>
                        <div style={{ marginTop: '10px' }}>
                            <span className='c-default fs16-fw500'>New Password:</span>
                            <Input.Password className='bg-root c-default' autoComplete="none" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                        </div>
                        <div style={{ marginTop: '10px' }}>
                            <span className='c-default fs16-fw500'>Confirm New Password:</span>
                            <Input.Password className='bg-root c-default' autoComplete="none" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} />
                        </div>
                        {
                            changePasswordError &&
                            <p className='c-red fs16-fw500' style={{ marginTop: '10px' }}>{changePasswordError}</p>
                        }
                    </Modal>
                ): (
                    <Modal
                        title={displayName}
                        style={{
                            borderRadius: '16px',
                            padding: '0px',
                            boxShadow: '0px 10px 20px -8px #392EA433'
                        }}
                        className='custom-modal c-default'
                        okButtonProps={{
                            className: 'bg-primary br-16 c-root pt-2 pb-2'
                        }}
                        cancelButtonProps={{
                            className: 'bg-root br-16 c-primary b-primary pt-2 pb-2'
                        }}
                        open={isOpen}
                        onOk={() => {
                            handleChangePassword(oldPassword, newPassword, confirmNewPassword);
                        }}
                        onCancel={() => {
                            onCloseModal();
                            setOldPassword("");
                            setNewPassword("");
                            setConfirmNewPassword("");
                            setChangePasswordError("");
                        }}
                        okText="Change Password"
                        cancelText="Cancel"
                    >   
                        <button className='bg-white-a-50 c-default fs16-fw600 p-2 pe-3 ps-3 w-100 br-16' onClick={() => {
                            logOut().then((res) => {
                                appStateContext?.dispatch({ type: "SET_USER_LOGIN_STATUS", payload: undefined });
                                window.location.href = "/login";
                            });
                        }}>
                            Logout
                        </button>
                        <div className='ant-modal-title mt-4'>Change Password</div>
                        <p style={{
                            fontSize: "16px",
                            marginTop: "0px",
                            marginBottom: '16px',
                            fontFamily: 'inter'
                        }}>Please enter your new password</p>
                        <div>
                            <span className='c-default fs16-fw500'>Old Password:</span>
                            <Input.Password className='bg-root c-default' autoComplete="none" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} />
                        </div>
                        <div style={{ marginTop: '10px' }}>
                            <span className='c-default fs16-fw500'>New Password:</span>
                            <Input.Password className='bg-root c-default' autoComplete="none" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                        </div>
                        <div style={{ marginTop: '10px' }}>
                            <span className='c-default fs16-fw500'>Confirm New Password:</span>
                            <Input.Password className='bg-root c-default' autoComplete="none" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} />
                        </div>
                        {
                            changePasswordError &&
                            <p className='c-red fs16-fw500' style={{ marginTop: '10px' }}>{changePasswordError}</p>
                        }
                    </Modal>
                )
            }
        </>
        
    );
}

export default ChangePasswordModal